/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {forwardRef, useEffect, useImperativeHandle, useRef, useState,} from 'react'
import {Bars3BottomLeftIcon, BoldIcon, ItalicIcon, LinkIcon, ListBulletIcon,} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'

interface UiRichTextEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    contentClassName?: string
    error?: string
    onFocus?: () => void
}

export interface UiRichTextEditorRef {
    insertAtCursor: (text: string) => void
}

const UiRichTextEditor = forwardRef<UiRichTextEditorRef, UiRichTextEditorProps>(
    ({ value, onChange, placeholder, className, contentClassName, error, onFocus }, ref) => {
        const { t } = useTranslation()
        const editorRef = useRef<HTMLDivElement>(null)
        const isUpdatingRef = useRef(false)
        const lastRangeRef = useRef<Range | null>(null)
        const [activeFormats, setActiveFormats] = useState<Set<string>>(
            new Set()
        )

        useEffect(() => {
            if (editorRef.current && !isUpdatingRef.current) {
                const currentHtml = editorRef.current.innerHTML
                if (currentHtml !== value) {
                    editorRef.current.innerHTML = value || ''
                }
            }
        }, [value])

        const updateActiveFormats = () => {
            const formats = new Set<string>()
            if (document.queryCommandState('bold')) formats.add('bold')
            if (document.queryCommandState('italic')) formats.add('italic')
            if (document.queryCommandState('insertUnorderedList'))
                formats.add('ul')
            if (document.queryCommandState('insertOrderedList'))
                formats.add('ol')
            setActiveFormats(formats)
        }

        const handleInput = () => {
            if (editorRef.current) {
                isUpdatingRef.current = true
                const html = editorRef.current.innerHTML
                onChange(html)
                updateActiveFormats()
                setTimeout(() => {
                    isUpdatingRef.current = false
                }, 0)
            }
        }

        const handleSelectionChange = () => {
            updateActiveFormats()
            const selection = window.getSelection()
            if (
                selection &&
                selection.rangeCount > 0 &&
                editorRef.current?.contains(
                    selection.getRangeAt(0).commonAncestorContainer
                )
            ) {
                lastRangeRef.current = selection.getRangeAt(0).cloneRange()
            }
        }

        useEffect(() => {
            document.addEventListener('selectionchange', handleSelectionChange)
            return () => {
                document.removeEventListener(
                    'selectionchange',
                    handleSelectionChange
                )
            }
        }, [])

        const execCommand = (command: string, value?: string) => {
            editorRef.current?.focus()
            document.execCommand(command, false, value)
            setTimeout(() => {
                handleInput()
            }, 10)
        }

        const createLink = () => {
            const url = prompt('Enter URL:')
            if (url) {
                execCommand('createLink', url)
            }
        }

        useImperativeHandle(ref, () => ({
            insertAtCursor: (text: string) => {
                if (!editorRef.current) return

                editorRef.current.focus()

                const selection = window.getSelection()
                if (!selection) return

                let range: Range

                if (
                    lastRangeRef.current &&
                    editorRef.current.contains(
                        lastRangeRef.current.commonAncestorContainer
                    )
                ) {
                    range = lastRangeRef.current
                } else if (selection.rangeCount > 0) {
                    range = selection.getRangeAt(0)
                } else {
                    range = document.createRange()
                    range.selectNodeContents(editorRef.current)
                    range.collapse(false)
                }

                selection.removeAllRanges()
                selection.addRange(range)

                const textNode = document.createTextNode(text)
                range.deleteContents()
                range.insertNode(textNode)

                range.setStartAfter(textNode)
                range.setEndAfter(textNode)
                selection.removeAllRanges()
                selection.addRange(range)

                lastRangeRef.current = range.cloneRange()

                setTimeout(() => {
                    handleInput()
                }, 10)
            },
        }))

        const toolbarButtonClass = (format: string) =>
            clsx(
                'p-1 rounded-md transition-all duration-200',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'active:scale-95',

                activeFormats.has(format) &&
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            )

        const iconClass = (format: string) =>
            clsx(
                'size-4 transition-colors',
                activeFormats.has(format)
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
            )

        return (
            <div
                className={clsx(
                    'UiRichTextEditor flex flex-col gap-0',
                    className
                )}
            >
                <div className="bg-gradient-to-b from-gray-50 to-gray-100/50 dark:from-gray-800 dark:to-gray-800/50 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-t-xl flex flex-wrap gap-1.5 shadow-sm">
                    <button
                        type="button"
                        title="Bold (Ctrl+B)"
                        onClick={() => execCommand('bold')}
                        className={toolbarButtonClass('bold')}
                    >
                        <BoldIcon className={iconClass('bold')} />
                    </button>
                    <button
                        type="button"
                        title="Italic (Ctrl+I)"
                        onClick={() => execCommand('italic')}
                        className={toolbarButtonClass('italic')}
                    >
                        <ItalicIcon className={iconClass('italic')} />
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 my-auto mx-0.5" />
                    <button
                        type="button"
                        title={t('Insert Link')}
                        onClick={createLink}
                        className={toolbarButtonClass('link')}
                    >
                        <LinkIcon className={iconClass('link')} />
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 my-auto mx-0.5" />
                    <button
                        type="button"
                        title={t('Bullet List')}
                        onClick={() => execCommand('insertUnorderedList')}
                        className={toolbarButtonClass('ul')}
                    >
                        <ListBulletIcon className={iconClass('ul')} />
                    </button>
                    <button
                        type="button"
                        title={t('Numbered List')}
                        onClick={() => execCommand('insertOrderedList')}
                        className={toolbarButtonClass('ol')}
                    >
                        <Bars3BottomLeftIcon className={iconClass('ol')} />
                    </button>
                </div>
                <div
                    ref={(node) => {
                        if (node) {
                            editorRef.current = node
                        }
                    }}
                    contentEditable
                    onInput={handleInput}
                    onFocus={onFocus}
                    className={clsx(
                        'UiRichTextEditorContent',
                        'w-full p-4 text-sm grow',
                        'bg-white dark:bg-gray-900',
                        'text-gray-900 dark:text-gray-100',
                        'border border-t-0 border-gray-300 dark:border-gray-600',
                        'rounded-b-xl outline-none',
                        'focus:border-primary-600 dark:focus:border-primary-600',
                        'transition-all duration-200',
                        '[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-400 dark:[&:empty]:before:text-gray-500 [&:empty]:before:pointer-events-none',
                        contentClassName
                    )}
                    data-placeholder={placeholder}
                    suppressContentEditableWarning
                />
                {error && (
                    <span className="text-xs text-red-500 dark:text-red-400 mt-1.5 ml-1">
                        {error}
                    </span>
                )}
            </div>
        )
    }
)

UiRichTextEditor.displayName = 'UiRichTextEditor'

export default UiRichTextEditor
